import { RefObject, useEffect } from 'react';

export const X_EMBED_SELECTOR = 'blockquote.twitter-tweet';

interface XWidgets {
  ready?: (callback: () => void) => void;
  widgets?: { load: (element?: HTMLElement) => void };
}

declare global {
  interface Window {
    twttr?: XWidgets;
    loadXWidgets?: () => void;
  }
}

/**
 * Re-scans a rich-text container for X (Twitter) post embeds.
 *
 * widgets.js scans the DOM once, when it loads. Content mounted after that —
 * by client-side navigation, or by the lazy CMS rich-text block — is never
 * converted, which is why embeds only appeared after a hard refresh. X's CMS
 * guidance requires calling twttr.widgets.load() after inserting content.
 *
 * The script itself comes from the tenant's index.html (X's official async
 * bootstrap), which defines twttr.ready() synchronously and queues callbacks
 * until widgets.js has loaded, so this never races it. A tenant whose
 * index.html omits the bootstrap simply no-ops here.
 *
 * window.loadXWidgets() (also defined by that bootstrap) injects the
 * widgets.js <script> tag itself — it's called here, rather than eagerly on
 * every page, so a page with no embed never issues a request to
 * platform.x.com.
 */
export const useXEmbeds = <T extends HTMLElement>(containerRef: RefObject<T | null>, content?: string): void => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !container.querySelector(X_EMBED_SELECTOR)) {
      return;
    }

    let cancelled = false;

    window.loadXWidgets?.();

    window.twttr?.ready?.(() => {
      // Re-read the ref: the container can be detached while the script loads.
      const current = containerRef.current;
      if (cancelled || !current) {
        return;
      }
      window.twttr?.widgets?.load(current);
    });

    return () => {
      cancelled = true;
    };
  }, [containerRef, content]);
};
